import { DeleteOutlined, EditOutlined, FileTextOutlined } from '@ant-design/icons';
import { Button, Table, Modal, Form, Card, Descriptions, Space, Input, Select } from 'antd';
import Title from 'antd/es/typography/Title';
import ColeccionItemForm from 'components/forms/ColeccionItemForm';
import ColeccionItemImageThumb from 'components/forms/ColeccionItemImageThumb';
import { useColeccion } from 'providers/ColeccionProvider';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import paths from 'assets/routes/paths';
import useTenantFlags from 'hooks/useTenantFlags';

const IDColeccionPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAllItems, createItem, updateItem, deleteItem, isLoading } = useColeccion();
  const { readOnly } = useTenantFlags();

  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [tipoItem, setTipoItem] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [coleccion, setColeccion] = useState(null);
  const [form] = Form.useForm();
  const imageUploadRef = useRef(null);

  const fetchItems = useCallback(async () => {
    const result = await getAllItems(id);
    setItems(result.items);
    setColeccion(result.coleccion);
  }, [getAllItems, id]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const showModal = (item = null) => {
    setCurrentItem(item);
    const initialFormValues = item || null;
    if (coleccion.type === 'TEXTOS') {
      delete initialFormValues.cantidad;
      delete initialFormValues.precio;
      delete initialFormValues.costo;
    }
    form.setFieldsValue(initialFormValues);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setCurrentItem(null);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      // image_key se orquesta aparte via imageUploadRef.uploadPending().
      // No viene en values porque el campo no forma parte del form state.
      let targetItemId = currentItem?.id ?? null;
      if (currentItem) {
        await updateItem(currentItem.id, values);
      } else {
        const created = await createItem(id, values);
        targetItemId = created?.id ?? null;
      }
      if (targetItemId && imageUploadRef.current?.hasPending?.()) {
        try {
          await imageUploadRef.current.uploadPending(targetItemId);
        } catch (imgErr) {
          console.warn('No se pudo subir la foto tras crear el item', imgErr);
        }
      }
      const updatedItems = await getAllItems(id);
      setItems(updatedItems.items);
      setIsModalVisible(false);
      setCurrentItem(null);
    } catch (formErr) {
      console.warn('Validación del form fallo', formErr);
    }
  };

  const handleDelete = async (itemId) => {
    Modal.confirm({
      title: '¿Está seguro que desea eliminar este ítem?',
      okText: 'Sí',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        await deleteItem(itemId);
        const updatedItems = await getAllItems(id);
        setItems(updatedItems.items);
      },
    });
  };

  const columns = [];

  if (coleccion?.type === 'PRODUCTOS') {
    columns.push({
      title: 'Foto',
      key: 'image_key',
      width: 64,
      render: (_, record) => (
        <ColeccionItemImageThumb itemId={record.id} imageKey={record.image_key} />
      ),
    });
  }

  columns.push(
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
    },
    {
      title: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
    }
  );

  if (coleccion?.type === 'PRODUCTOS') {
    columns.push(
      {
        title: 'Cantidad',
        dataIndex: 'cantidad',
        key: 'cantidad',
      },
      {
        title: 'Precio',
        dataIndex: 'precio',
        key: 'precio',
      },
      {
        title: 'Costo',
        dataIndex: 'costo',
        key: 'costo',
      },
      {
        title: 'Tipo de Ítem',
        dataIndex: 'tipo_item',
        key: 'tipo_item',
      }
    );
  }

  columns.push({
    title: 'Acciones',
    key: 'acciones',
    render: (_, record) => (
      <Space>
        {coleccion?.type === 'ACTIVIDADES' && (
          <Button
            type="primary"
            onClick={() =>
              navigate(`${paths.admin.colecciones.items(id, record.id)}?type=ACTIVIDADES`)
            }
            icon={<FileTextOutlined />}
          >
            Actividades
          </Button>
        )}

        {!readOnly && (
          <Button
            type="default"
            className="!border-primary text-primary"
            onClick={() => showModal(record)}
            icon={<EditOutlined />}
          >
            {/* Editar */}
          </Button>
        )}
        {!readOnly && (
          <Button
            type="default"
            onClick={() => handleDelete(record.id)}
            danger
            icon={<DeleteOutlined />}
          >
            {/* Eliminar */}
          </Button>
        )}
        {(coleccion?.type === 'PRODUCTOS' || coleccion?.type === 'TEXTOS') && (
          <Button
            type="primary"
            onClick={() => navigate(paths.admin.documentComposer(record.id, 'presupuesto'))}
            icon={<FileTextOutlined />}
          >
            Componer Documento
          </Button>
        )}
      </Space>
    ),
  });

  // Filtros client-side: buscador (nombre/descripción) + tipo de ítem (PRODUCTOS).
  const isProductos = coleccion?.type === 'PRODUCTOS';
  const tipoItemOptions = useMemo(() => {
    const set = new Set((items ?? []).map((i) => i.tipo_item).filter(Boolean));
    return [...set].sort((a, b) => a.localeCompare(b)).map((t) => ({ value: t, label: t }));
  }, [items]);
  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (items ?? []).filter((i) => {
      const matchQ =
        !q || [i.nombre, i.descripcion].some((v) => (v || '').toLowerCase().includes(q));
      const matchTipo = !tipoItem || i.tipo_item === tipoItem;
      return matchQ && matchTipo;
    });
  }, [items, search, tipoItem]);

  return (
    <div className="space-y-4">
      {coleccion && (
        <Card title="Detalle de la Colección" className="mb-4">
          <Descriptions bordered column={1}>
            <Descriptions.Item label="Nombre">{coleccion.nombre}</Descriptions.Item>
            <Descriptions.Item label="Descripción">{coleccion.descripcion}</Descriptions.Item>
            <Descriptions.Item label="Tipo">{coleccion.type}</Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      <Card className="welcome col-span-3">
        <div className="flex flex-row justify-between">
          <Title level={3} className="text-start">
            <span className="uppercase text-white">Ítems de la Colección</span>
          </Title>
          {!readOnly && (
            <Button type="primary" onClick={() => showModal()} className="mb-4">
              Agregar Item
            </Button>
          )}
        </div>
      </Card>

      <Space wrap className="mb-2">
        <Input.Search
          allowClear
          placeholder="Buscar por nombre o descripción…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 280 }}
        />
        {isProductos && tipoItemOptions.length > 0 && (
          <Select
            allowClear
            showSearch
            placeholder="Tipo de ítem"
            value={tipoItem}
            onChange={setTipoItem}
            options={tipoItemOptions}
            optionFilterProp="label"
            style={{ width: 200 }}
          />
        )}
      </Space>

      <Table dataSource={filteredItems} columns={columns} rowKey="id" loading={isLoading} />
      <Modal
        title={currentItem ? 'Editar Item' : 'Agregar Item'}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        <ColeccionItemForm
          form={form}
          imageUploadRef={imageUploadRef}
          itemId={currentItem?.id ?? null}
          initialValues={
            currentItem || {
              nombre: '',
              descripcion: '',
              cantidad: 0,
              precio: 0,
              costo: 0,
              tipo_item:
                coleccion?.type === 'ACTIVIDADES'
                  ? 'ACTIVIDAD'
                  : coleccion?.type === 'PRODUCTOS'
                  ? 'PRODUCTO'
                  : 'TEXTO',
            }
          }
          visibleFields={
            coleccion?.type === 'TEXTOS'
              ? ['nombre', 'descripcion']
              : coleccion?.type === 'ACTIVIDADES'
              ? ['nombre', 'descripcion', 'tipo_item']
              : ['nombre', 'descripcion', 'cantidad', 'precio', 'costo', 'tipo_item', 'image']
          }
        />
      </Modal>
    </div>
  );
};

export default IDColeccionPage;
